import { useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { Upload, Trash2 } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Checkbox } from "@/components/ui/checkbox";
import { Switch } from "@/components/ui/switch";
import { Button } from "@/components/ui/button";
import { api, formatError } from "@/lib/api";
import { WEEKDAYS } from "@/lib/format";
import { Field } from "@/components/Field";
import { RoomPhoto } from "@/components/RoomPhoto";
import { FormDialog, useSubmit, num, clean } from "./FormDialog";

const EMPTY = { name: "", description: "", address: "", capacity: "", closed_weekdays: [], rules: "", keys_info: "", price_per_day: "", cleaning_enabled: false, cleaning_price: "", active: true };

const PhotoField = ({ room, onChange }) => {
  const ref = useRef(null);
  const [busy, setBusy] = useState(false);
  const run = async (fn, ok) => {
    setBusy(true);
    try { onChange((await fn()).data); toast.success(ok); } catch (e) { toast.error(formatError(e)); } finally { setBusy(false); }
  };
  const upload = (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    const fd = new FormData();
    fd.append("file", file);
    run(() => api.post(`/rooms/${room.id}/photo`, fd), "Foto caricata");
  };
  return (
    <Field label="Foto della sala" hint="Mostrata a chi prenota (JPG, PNG o WEBP, max 5 MB)">
      <div className="flex items-center gap-3">
        <RoomPhoto room={room} className="h-20 w-32" />
        <Button type="button" data-testid="room-photo-upload" variant="outline" className="h-11" disabled={busy} onClick={() => ref.current?.click()}><Upload className="h-4 w-4 mr-1" /> {room.has_photo ? "Cambia" : "Carica"}</Button>
        {room.has_photo && <Button type="button" data-testid="room-photo-delete" variant="ghost" className="h-11 text-red-700" disabled={busy} onClick={() => run(() => api.delete(`/rooms/${room.id}/photo`), "Foto rimossa")}><Trash2 className="h-4 w-4" /></Button>}
        <input ref={ref} type="file" accept="image/jpeg,image/png,image/webp" hidden data-testid="room-photo-input" onChange={upload} />
      </div>
    </Field>
  );
};

export const RoomDialog = ({ open, onOpenChange, room, onSaved }) => {
  const [f, setF] = useState(EMPTY);
  const [cur, setCur] = useState(room);
  const { saving, run } = useSubmit();
  const set = (k) => (v) => setF((s) => ({ ...s, [k]: v?.target ? v.target.value : v }));

  useEffect(() => {
    if (open) {
      setF(room ? { ...EMPTY, ...Object.fromEntries(Object.keys(EMPTY).map((k) => [k, room[k] ?? EMPTY[k]])) } : EMPTY);
      setCur(room);
    }
  }, [open, room]);

  const toggleDay = (i) => setF((s) => ({ ...s, closed_weekdays: s.closed_weekdays.includes(i) ? s.closed_weekdays.filter((x) => x !== i) : [...s.closed_weekdays, i] }));

  const submit = () =>
    run(async () => {
      const body = clean({ ...f, capacity: num(f.capacity), price_per_day: num(f.price_per_day), cleaning_price: f.cleaning_enabled ? num(f.cleaning_price) : null });
      const { data } = cur ? await api.patch(`/rooms/${cur.id}`, body) : await api.post("/rooms", body);
      onOpenChange(false);
      onSaved?.(data);
    }, cur ? "Sala aggiornata" : "Sala creata");

  return (
    <FormDialog open={open} onOpenChange={onOpenChange} title={room ? "Modifica sala" : "Nuova sala"} onSubmit={submit} saving={saving} testId="room-dialog" wide>
      <div className="grid grid-cols-2 gap-3">
        <Field label="Nome *"><Input data-testid="room-name-input" required value={f.name} onChange={set("name")} className="h-11" /></Field>
        <Field label="Capienza massima"><Input data-testid="room-capacity-input" type="number" min="1" value={f.capacity} onChange={set("capacity")} className="h-11" /></Field>
      </div>
      <Field label="Indirizzo"><Input data-testid="room-address-input" value={f.address} onChange={set("address")} className="h-11" /></Field>
      {cur ? <PhotoField room={cur} onChange={(r) => { setCur(r); onSaved?.(r, true); }} /> : <p className="text-sm text-slate-500">Potrai caricare la foto dopo aver creato la sala.</p>}
      <div className="grid grid-cols-2 gap-3">
        <Field label="Prezzo al giorno (CHF)" hint="Vuoto = nessun prezzo"><Input data-testid="room-price-day" type="number" min="0" step="0.05" value={f.price_per_day} onChange={set("price_per_day")} className="h-11" /></Field>
        <div className="space-y-2">
          <label className="flex items-center gap-3 text-sm font-medium pt-7"><Switch data-testid="room-cleaning-switch" checked={f.cleaning_enabled} onCheckedChange={set("cleaning_enabled")} /> Offri la pulizia</label>
        </div>
      </div>
      {f.cleaning_enabled && <Field label="Prezzo pulizia (CHF) *"><Input data-testid="room-cleaning-price" type="number" min="0" step="0.05" required value={f.cleaning_price} onChange={set("cleaning_price")} className="h-11" /></Field>}
      <Field label="Giorni di chiusura">
        <div className="flex flex-wrap gap-2">
          {WEEKDAYS.map((d, i) => (
            <label key={d} className="flex items-center gap-2 rounded-md border px-3 h-10 text-sm cursor-pointer">
              <Checkbox data-testid={`room-closed-${i}`} checked={f.closed_weekdays.includes(i)} onCheckedChange={() => toggleDay(i)} /> {d}
            </label>
          ))}
        </div>
      </Field>
      <Field label="Descrizione"><Textarea data-testid="room-description-input" value={f.description} onChange={set("description")} rows={2} /></Field>
      <Field label="Regolamento" hint="Ogni modifica crea una nuova versione"><Textarea data-testid="room-rules-input" value={f.rules} onChange={set("rules")} rows={4} /></Field>
      <Field label="Informazioni sulle chiavi"><Input data-testid="room-keys-input" value={f.keys_info} onChange={set("keys_info")} className="h-11" /></Field>
      <label className="flex items-center gap-3"><Switch data-testid="room-active-switch" checked={f.active} onCheckedChange={set("active")} /> Sala disponibile</label>
    </FormDialog>
  );
};
